<!-- The host dialog only has a keyed toast, which will not render our messages. -->
<p-toast position="top-right" [baseZIndex]="11000"></p-toast>

<!-- Shown on every tab, so the gap is visible the moment the dialog opens. -->
<div class="sc-gap-warning" *ngIf="missingLibraryIndexes.length">
  <strong>Configured in the library but not in {{ ownerLabel }}:</strong>
  {{ missingLibraryIndexes.join(", ") }}.
  <button type="button" class="sc-gap-action" (click)="addMissingFromLibrary()">
    Add them here
  </button>
</div>

<div class="sc-gap-note" *ngIf="libraryCheckState === 'unavailable'">
  <strong>Library cross-check unavailable.</strong> {{ libraryCheckDetail }}
</div>

<div class="sc-panel" *ngIf="tab !== 'parameters'">

  <!-- ===================== Module Target ===================== -->
  <ng-container *ngIf="tab === 'target'">
    <section class="sc-section">
      <div class="sc-section-head">
        <div>
          <h6 class="sc-section-title">Aspect</h6>
          <p class="sc-section-hint">Select one or more aspects this module target is optimised for.</p>
        </div>
        <span class="sc-save-chip" *ngIf="saveState('target') !== 'idle'"
          [ngClass]="saveState('target')" (click)="flushSave('target')">
          {{ saveLabel('target') }}
        </span>
      </div>

      <div class="sc-aspect-select" #aspectDropdown>
        <button type="button" class="sc-aspect-trigger" [class.open]="aspectDropdownOpen"
          (click)="toggleAspectDropdown()">
          <span class="sc-aspect-values">
            <span class="sc-aspect-chip" *ngFor="let aspect of activeAspects">{{ aspect }}</span>
            <span class="sc-placeholder" *ngIf="!activeAspects.length">Select aspects</span>
          </span>
          <span class="sc-caret">▾</span>
        </button>

        <div class="sc-aspect-panel" *ngIf="aspectDropdownOpen">
          <div class="sc-aspect-options">
            <div class="sc-aspect-row" *ngFor="let aspect of aspectOptions">
              <button type="button" class="sc-aspect-option" [class.active]="isAspectSelected(aspect)"
                (click)="toggleAspect(aspect)">
                <span class="sc-check" [class.on]="isAspectSelected(aspect)"></span>
                <span>{{ aspect }}</span>
              </button>
              <button type="button" class="sc-aspect-delete" *ngIf="isCustomAspect(aspect)"
                [title]="'Delete the ' + aspect + ' aspect'" (click)="deleteAspect(aspect, $event)">✕</button>
            </div>
          </div>
          <div class="sc-aspect-footer">
            <div class="sc-aspect-create">
              <input type="text" class="sc-input" placeholder="Create new aspect" maxlength="40"
                [(ngModel)]="newAspectName" (keydown.enter)="createAspect()" />
              <button type="button" class="sc-btn sc-btn-primary" [disabled]="!newAspectName.trim()"
                (click)="createAspect()">+</button>
            </div>
            <button type="button" class="sc-aspect-clear" [disabled]="!activeAspects.length"
              (click)="clearAspects()">✕ Clear all</button>
          </div>
        </div>
      </div>
    </section>

    <section class="sc-section">
      <div class="sc-section-head">
        <div>
          <h6 class="sc-section-title">Module KPIs</h6>
          <p class="sc-section-hint">
            {{ targetSelectionSummary }}
            <span class="sc-filter-note" *ngIf="targetFilterSummary">{{ targetFilterSummary }}</span>
          </p>
        </div>
        <div class="sc-actions" *ngIf="targetRows.length">
          <div class="sc-search" *ngIf="selectedTargetRows.length">
            <input type="text" class="sc-input" placeholder="Search parameters" [(ngModel)]="targetSearchTerm" />
            <button type="button" class="sc-search-clear" *ngIf="isTargetSearchActive"
              title="Clear search" (click)="clearTargetSearch()">✕</button>
          </div>
          <button type="button" class="sc-btn sc-btn-primary" [disabled]="!availableTargetRows.length"
            [title]="availableTargetRows.length
              ? 'Choose which of this module\'s parameters to analyse'
              : 'Every parameter configured on this module is already on the list'"
            (click)="openAddParameters()">+ Add Parameter</button>
        </div>
      </div>

      <div class="sc-table-frame" *ngIf="selectedTargetRows.length; else noTargets">
        <table class="sc-table">
          <thead>
            <tr>
              <th class="sc-col-check">
                <button type="button" class="sc-row-check" [class.on]="allTargetsSelected"
                  [class.partial]="someTargetsSelected"
                  title="Take every parameter shown off the list"
                  (click)="toggleAllTargets()"></button>
              </th>
              <th class="sc-col-name">Parameter</th>
              <th class="sc-col-code">Code</th>
              <th>Min</th>
              <th>Max</th>
              <th>Design</th>
              <th>Price</th>
              <th class="sc-col-aspect">Aspect</th>
              <th class="sc-col-analysis">Analysis</th>
              <th class="sc-col-remove"></th>
            </tr>
          </thead>
          <tbody>
            <tr class="sc-no-match" *ngIf="!filteredTargetRows.length">
              <td colspan="10">No parameters match “{{ targetSearchTerm }}”.</td>
            </tr>
            <tr *ngFor="let row of filteredTargetRows; trackBy: trackTargetRow" [class.selected]="row.selected">
              <td class="sc-col-check">
                <button type="button" class="sc-row-check" [class.on]="row.selected"
                  title="Take this parameter off the list. Its values are kept, so adding it back restores them."
                  (click)="toggleTargetSelection(row)"></button>
              </td>
              <td class="sc-col-name">
                <span class="sc-param-name">{{ row.parameter_name }}</span>
                <span class="sc-param-unit" *ngIf="row.parameter_unit">{{ row.parameter_unit }}</span>
              </td>
              <td class="sc-col-code"><span class="sc-code">{{ row.parameter_code }}</span></td>
              <td><input type="number" class="sc-input" placeholder="—" [(ngModel)]="row.target_min" (ngModelChange)="onTargetChanged()" /></td>
              <td><input type="number" class="sc-input" placeholder="—" [(ngModel)]="row.target_max" (ngModelChange)="onTargetChanged()" /></td>
              <td><input type="number" class="sc-input" placeholder="—" [(ngModel)]="row.design_value" (ngModelChange)="onTargetChanged()" /></td>
              <td><input type="number" class="sc-input" placeholder="—" [(ngModel)]="row.price" (ngModelChange)="onTargetChanged()" /></td>
              <td class="sc-col-aspect">
                <select class="sc-input" [ngModel]="row.aspect" (ngModelChange)="onTargetAspectChanged(row, $event)">
                  <option value="">—</option>
                  <option *ngFor="let aspect of aspectOptions" [value]="aspect">{{ aspect }}</option>
                </select>
              </td>
              <td class="sc-col-analysis">
                <div class="sc-analysis">
                  <button *ngFor="let mode of analysisModes" type="button" class="sc-chip"
                    [class.on]="row.analysis[mode.id]" (click)="toggleAnalysisMode(row, mode.id)">
                    {{ mode.label }}
                  </button>
                </div>
              </td>
              <td class="sc-col-remove">
                <button type="button" class="sc-remove" title="Remove this KPI from the target"
                  (click)="removeTargetRow(row)">🗑</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <ng-template #noTargets>
        <p class="sc-empty" *ngIf="!targetRows.length">This module has no parameters configured yet.</p>
        <p class="sc-empty" *ngIf="targetRows.length">
          No parameters on the KPI list yet. Use <strong>Add Parameter</strong> to pick from the
          {{ targetRows.length }} configured on this module.
        </p>
      </ng-template>
    </section>

    <!-- Add Parameter: the KPI list above shows only what has been added, so this is
         where the rest of the module's parameters are reachable from. -->
    <div class="sc-picker-backdrop" *ngIf="addParamVisible" (click)="closeAddParameters()">
      <div class="sc-picker" (click)="$event.stopPropagation()">
        <div class="sc-picker-head">
          <h5 class="sc-picker-heading">Add Parameter</h5>
          <button type="button" class="sc-picker-close" title="Close" (click)="closeAddParameters()">✕</button>
        </div>
        <p class="sc-section-hint">
          Pick the parameters to analyse on this module. To add one that is not listed here,
          configure it first on <strong>Configure Parameter</strong>.
        </p>

        <div class="sc-picker-tools" *ngIf="availableTargetRows.length">
          <div class="sc-search">
            <input type="text" class="sc-input" placeholder="Search parameters" [(ngModel)]="addParamSearchTerm" />
            <button type="button" class="sc-search-clear" *ngIf="isAddParamSearchActive"
              title="Clear search" (click)="clearAddParamSearch()">✕</button>
          </div>
          <button type="button" class="sc-btn" [disabled]="!filteredAvailableRows.length"
            (click)="toggleAllAddParams()">
            {{ allAddParamsSelected ? 'Clear selection' : (isAddParamSearchActive ? 'Select all matches' : 'Select all') }}
          </button>
        </div>

        <div class="sc-table-frame sc-picker-frame" *ngIf="availableTargetRows.length; else noneAvailable">
          <table class="sc-table sc-picker-table">
            <thead>
              <tr>
                <th class="sc-col-check">
                  <button type="button" class="sc-row-check" [class.on]="allAddParamsSelected"
                    [class.partial]="someAddParamsSelected" (click)="toggleAllAddParams()"></button>
                </th>
                <th class="sc-col-name">Parameter</th>
                <th class="sc-col-code">Code</th>
              </tr>
            </thead>
            <tbody>
              <tr class="sc-no-match" *ngIf="!filteredAvailableRows.length">
                <td colspan="3">No parameters match “{{ addParamSearchTerm }}”.</td>
              </tr>
              <tr *ngFor="let row of filteredAvailableRows; trackBy: trackTargetRow"
                [class.selected]="isAddParamSelected(row)" (click)="toggleAddParam(row)">
                <td class="sc-col-check">
                  <button type="button" class="sc-row-check" [class.on]="isAddParamSelected(row)"></button>
                </td>
                <td class="sc-col-name">
                  <span class="sc-param-name">{{ row.parameter_name }}</span>
                  <span class="sc-param-unit" *ngIf="row.parameter_unit">{{ row.parameter_unit }}</span>
                </td>
                <td class="sc-col-code"><span class="sc-code">{{ row.parameter_code }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>

        <ng-template #noneAvailable>
          <p class="sc-empty">
            Every parameter configured on this module is already on the KPI list.
          </p>
        </ng-template>

        <div class="sc-picker-actions">
          <span class="sc-picker-count" *ngIf="availableTargetRows.length">
            {{ addParamSelectionCount }} of {{ availableTargetRows.length }} selected
          </span>
          <button type="button" class="sc-btn" (click)="closeAddParameters()">Cancel</button>
          <button type="button" class="sc-btn sc-btn-primary" [disabled]="!addParamSelectionCount"
            (click)="confirmAddParameters()">
            Add{{ addParamSelectionCount ? " " + addParamSelectionCount : "" }}
          </button>
        </div>
      </div>
    </div>
  </ng-container>

  <!-- ===================== Module Indexes ===================== -->
  <ng-container *ngIf="tab === 'indexes'">
    <section class="sc-section">
      <div class="sc-section-head">
        <div>
          <h6 class="sc-section-title">Module Indexes</h6>
          <p class="sc-section-hint">
            Select the indexes to compute and set their weightage.
            Total weightage of selected indexes must equal 100.
          </p>
        </div>
        <div class="sc-actions">
          <span class="sc-save-chip" *ngIf="saveState('index') !== 'idle'"
            [ngClass]="saveState('index')" (click)="flushSave('index')">{{ saveLabel('index') }}</span>
          <span class="sc-total" [class.valid]="isIndexWeightageValid" [class.invalid]="!isIndexWeightageValid">
            Total weightage: {{ totalIndexWeightage }}%
          </span>
          <button type="button" class="sc-btn" [disabled]="!selectedIndexRows.length" (click)="distributeEvenly()">
            Distribute evenly
          </button>
          <button type="button" class="sc-btn" [disabled]="creatingFormula"
            title="Compile each Formula into its PureBI form by replacing parameter names with codes. Parameters on another unit are written unit-qualified, e.g. 1B:1B22"
            (click)="createFormula()">{{ creatingFormula ? "Working…" : "Create Formula" }}</button>
          <button type="button" class="sc-btn sc-btn-primary" (click)="addIndexRow()">+ Create Indexes</button>
        </div>
      </div>
      <div class="sc-table-frame" *ngIf="indexRows.length; else noIndexes">
        <table class="sc-table sc-index-table">
          <thead>
            <tr>
              <th class="sc-col-check">
                <button type="button" class="sc-row-check" [class.on]="allIndexesSelected"
                  [class.partial]="someIndexesSelected" (click)="toggleAllIndexes()"></button>
              </th>
              <th class="sc-col-code">Code</th>
              <th class="sc-col-name">Index</th>
              <th class="sc-col-formula">Formula</th>
              <th class="sc-col-formula">PureBI Formula</th>
              <th class="sc-col-aspect">Aspect</th>
              <th class="sc-col-weight">Weightage (%)</th>
              <th class="sc-col-health" title="Only one index per module can be the Health Index">Health Index</th>
              <th class="sc-col-remove"></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of indexRows; trackBy: trackIndexRow" [class.selected]="row.selected">
              <td class="sc-col-check">
                <button type="button" class="sc-row-check" [class.on]="row.selected"
                  (click)="toggleIndexSelection(row)"></button>
              </td>
              <td class="sc-col-code">
                <span class="sc-code" *ngIf="row.index_code; else noCode">{{ row.index_code }}</span>
                <ng-template #noCode>
                  <span class="sc-code sc-code-pending"
                    title="Assigned when this index is first saved. Until then it cannot be used in another formula.">
                    not saved
                  </span>
                </ng-template>
              </td>
              <td class="sc-col-name">
                <input type="text" class="sc-input sc-name-input" maxlength="80" placeholder="Index name"
                  [(ngModel)]="row.index_name" (ngModelChange)="onIndexChanged()" />
                <input type="text" class="sc-input sc-desc-input" maxlength="160" placeholder="Description"
                  [(ngModel)]="row.description" (ngModelChange)="onIndexChanged()" />
              </td>
              <td class="sc-col-formula">
                <input type="text" class="sc-input sc-mono" placeholder="Formula" [title]="row.formula"
                  [(ngModel)]="row.formula" (ngModelChange)="onIndexChanged(row)" />
              </td>
              <td class="sc-col-formula">
                <input type="text" class="sc-input sc-mono" placeholder="PureBI formula" [title]="row.purebi_formula"
                  [class.sc-input-stale]="row.stale"
                  [(ngModel)]="row.purebi_formula" (ngModelChange)="onPureBiChanged(row)" />
                <span class="sc-missing" *ngIf="row.unresolved?.length"
                  title="Not found among the parameters or indexes of any module in this plant">
                  Missing parameter: {{ row.unresolved.join(", ") }} — not converted
                </span>
                <span class="sc-stale" *ngIf="row.stale"
                  title="Compiled from an earlier version of the formula beside it">
                  The PureBI formula above is out of date.
                </span>
                <span class="sc-borrowed" *ngIf="row.borrowed?.length"
                  title="Owned by another unit, so the code is written unit-qualified">
                  From another unit: {{ row.borrowed.join(", ") }}
                </span>
              </td>
              <td class="sc-col-aspect">
                <select class="sc-input" [ngModel]="row.aspect" (ngModelChange)="onIndexAspectChanged(row, $event)">
                  <option value="">—</option>
                  <option *ngFor="let aspect of aspectOptions" [value]="aspect">{{ aspect }}</option>
                </select>
              </td>
              <td class="sc-col-weight">
                <div class="sc-weight">
                  <input type="number" min="0" max="100" class="sc-input" placeholder="0"
                    [(ngModel)]="row.weightage" (ngModelChange)="onIndexChanged()" />
                  <span class="sc-weight-unit">%</span>
                </div>
              </td>
              <td class="sc-col-health">
                <button type="button" class="sc-switch" role="switch" [class.on]="row.health_index"
                  [attr.aria-checked]="!!row.health_index"
                  [attr.aria-label]="'Health Index: ' + row.index_name"
                  [title]="row.health_index
                    ? 'This is the module\'s Health Index'
                    : 'Make this the Health Index. Only one index can be - the current one is switched off.'"
                  (click)="toggleHealthIndex(row)"></button>
              </td>
              <td class="sc-col-remove">
                <button type="button" class="sc-remove" title="Remove this index"
                  (click)="removeIndexRow(row)">🗑</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <ng-template #noIndexes>
        <p class="sc-empty">
          No indexes defined for this module yet. Use <strong>Create Indexes</strong> to add one.
        </p>
      </ng-template>
    </section>
  </ng-container>

  <!-- ===================== Behaviour & Finetuning ===================== -->
  <ng-container *ngIf="tab === 'behaviour'">
    <section class="sc-section">
      <div class="sc-section-head">
        <div>
          <h6 class="sc-section-title">Behaviour &amp; Finetuning</h6>
          <p class="sc-section-hint">
            Define how the module should behave and finetune its analysis for the selected aspects.
          </p>
        </div>
        <span class="sc-save-chip" *ngIf="saveState('behaviour') !== 'idle'"
          [ngClass]="saveState('behaviour')" (click)="flushSave('behaviour')">{{ saveLabel('behaviour') }}</span>
      </div>

      <div class="sc-behaviour-picker">
        <span class="sc-picker-title">Select aspect</span>
        <p class="sc-section-hint">
          Each aspect has its own behaviour definition and finetuning.
          Pick one, fill both fields, then switch to the next.
        </p>
        <div class="sc-behaviour-aspects" *ngIf="activeAspects.length; else noAspects">
          <button *ngFor="let aspect of activeAspects" type="button" class="sc-behaviour-aspect"
            [class.active]="behaviourAspect === aspect" (click)="setBehaviourAspect(aspect)">
            {{ aspect }}
          </button>
        </div>
      </div>

      <ng-container *ngIf="behaviourRecord">
        <div class="sc-divider"></div>

        <div class="sc-block">
          <h6 class="sc-block-title">1. Universal Behaviour Definition — {{ behaviourAspect }}</h6>
          <p class="sc-section-hint">Describe the behaviour the module should follow for the {{ behaviourAspect }} aspect.</p>
          <textarea class="sc-input sc-textarea" rows="5"
            placeholder="e.g., Prioritise stable operation over marginal gains; flag any parameter approaching 80% of its control limit…"
            [(ngModel)]="behaviourRecord.behaviour" (ngModelChange)="onBehaviourChanged()"></textarea>
        </div>

        <div class="sc-block">
          <h6 class="sc-block-title">2. Finetuning — {{ behaviourAspect }}</h6>
          <p class="sc-section-hint">Add finetuning instructions, thresholds, or rules specific to {{ behaviourAspect }}.</p>
          <textarea class="sc-input sc-textarea" rows="5"
            placeholder="e.g., Reduce sensitivity for seasonal temperature swings; treat recovery dips above 5% as critical…"
            [(ngModel)]="behaviourRecord.finetuning" (ngModelChange)="onBehaviourChanged()"></textarea>
        </div>
      </ng-container>

      <ng-template #noAspects>
        <p class="sc-empty">
          No aspects selected for this module yet. Choose them under <strong>Module Target → Aspect</strong>.
        </p>
      </ng-template>
    </section>
  </ng-container>

  <!-- Confirmation, self-contained so the host dialog needs no extra wiring -->
  <div class="sc-confirm-backdrop" *ngIf="confirmVisible">
    <div class="sc-confirm">
      <h5 class="sc-confirm-title">Delete {{ confirmTitle }}</h5>
      <p class="sc-confirm-body">
        Do you want to delete the {{ confirmTitle | lowercase }}
        <strong>{{ confirmTarget }}</strong>? This action cannot be undone.
      </p>
      <div class="sc-confirm-actions">
        <button type="button" class="sc-btn" (click)="resolveConfirm(false)">Cancel</button>
        <button type="button" class="sc-btn sc-btn-danger" (click)="resolveConfirm(true)">Delete</button>
      </div>
    </div>
  </div>
</div>
